/**
 * THE client approve path (verification-loop P2-U1 / UI-BRIEF §P2-U1).
 *
 * Every human accept/reject/edit affordance in the Vite shell routes through
 * these two functions — nothing else in `excellent/client` may call an approve
 * write directly. Both legs land on server doors that funnel
 * `recordHumanOverride` (`override.human`, `@excellent/core/live-sessions`):
 *
 *   · task review   → `taskActions.setStatus`   → captureStatusOverride
 *                     (excellent/server/routes/projects.ts)
 *   · worker run    → `verdicts.submit`         → recordWorkerVerdictOverride
 *                     (excellent/server/routes/verdicts.ts)
 *
 * So the UI can never silently approve: `measureSilentApproveRate` reads 0 with
 * the UI in the loop. Surfaces render the decision through the shared
 * `<Decision>` block (`@excellent/ui`) or WorkRow actions and hand the mutation
 * here — do NOT add a page-local approve, a second modal, or a bespoke API
 * (verifier DRY checklist rejects on sight).
 *
 * §5.5 instrument fields (`presented_as`, `wall_ms` / `presented_at_ms`) ride
 * optional on both legs so the override-as-diff queue can measure reviewer time
 * without a second write path.
 */

import { apiCall } from "@excellent/ui/api";
import { taskActions } from "@excellent/ui/api/contracts/task-actions";
import { verdicts } from "@excellent/ui/api/contracts/verdicts";

export type ReviewDecisionPath = "accept" | "reject" | "edit";

/** §5.5 OverrideRecord.presented_as. */
export type OverridePresentation = "diff" | "raw";

/** Optional §5.5 instrument fields the override-as-diff door fills in. */
export type OverrideInstrument = {
  presentedAs?: OverridePresentation;
  /** Reviewer wall-clock ms (presentation → decision). Prefer this when known. */
  wallMs?: number;
  /** Epoch ms the item was presented; server subtracts when wallMs omitted. */
  presentedAtMs?: number;
};

/**
 * Decide a task in `review` — the ship/verify moment. Accept lands `done`;
 * reject bounces to `in_progress` (the standard verifier bounce). The optional
 * reason rides into the `override.human` record as the structured reason text.
 *
 * When called from the override queue with instrument fields, the server also
 * clears `human_gated` (and appends the clearance marker) so the item leaves
 * the queue — including the #44753 done+gated strand class.
 */
export function decideTaskReview(input: {
  taskId: number;
  projectSlug: string;
  sectionSlug: string;
  decision: "accept" | "reject";
  reason?: string;
  instrument?: OverrideInstrument;
}): Promise<{ ok: true }> {
  const inst = input.instrument;
  return apiCall(taskActions.setStatus, {
    body: {
      projectSlug: input.projectSlug,
      sectionSlug: input.sectionSlug,
      taskId: input.taskId,
      status: input.decision === "accept" ? "done" : "in_progress",
      ...(input.reason?.trim() ? { reason: input.reason.trim() } : {}),
      ...(inst?.presentedAs ? { presented_as: inst.presentedAs } : {}),
      ...(typeof inst?.wallMs === "number" ? { wall_ms: inst.wallMs } : {}),
      ...(typeof inst?.presentedAtMs === "number"
        ? { presented_at_ms: inst.presentedAtMs }
        : {}),
    },
  });
}

/**
 * Decide a worker run in the verdict queue. `edit` is "accept with a
 * correction" and requires the corrected output body.
 */
export function decideWorkerRun(input: {
  runId: number;
  decision: ReviewDecisionPath;
  note?: string;
  editedOutput?: string;
  instrument?: OverrideInstrument;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const inst = input.instrument;
  return apiCall(verdicts.submit, {
    params: { id: String(input.runId) },
    body: {
      verdict: input.decision,
      ...(input.note?.trim() ? { note: input.note.trim() } : {}),
      ...(input.editedOutput != null ? { editedOutput: input.editedOutput } : {}),
      ...(inst?.presentedAs ? { presented_as: inst.presentedAs } : {}),
      ...(typeof inst?.wallMs === "number" ? { wall_ms: inst.wallMs } : {}),
      ...(typeof inst?.presentedAtMs === "number"
        ? { presented_at_ms: inst.presentedAtMs }
        : {}),
    },
  });
}

/**
 * The one reject/edit prompt both surfaces share (same idiom as the action
 * queue's existing `prompt()` reject). Returns undefined when the human
 * cancels — the caller must then NOT decide.
 */
export function promptDecisionReason(
  decision: "reject" | "edit",
): string | undefined {
  const label =
    decision === "reject"
      ? "Reason for rejecting (recorded on the override):"
      : "Corrected output (recorded as an edit override):";
  const answer = window.prompt(label);
  return answer == null ? undefined : answer;
}
